// src/pages/Category/AddCategory.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import categoryService from '../../services/category.service';
import '../../styles/AddPages.css';

const AddCategory = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    userName: '',
    companyId: '',
    companyName: '',
    categoryName: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
      
      let companyId = '';
      let companyName = 'No company selected';
      
      if (isSuperAdmin) {
        companyId = localStorage.getItem('selectedCompanyId') || '';
        
        const companyButton = document.querySelector('button[class*="company"]');
        if (companyButton) {
          companyName = companyButton.textContent?.trim() || 'No company selected';
        } else if (companyId) {
          companyName = `Company ID: ${companyId}`;
        }
      } else {
        companyId = user.companyId || '';
        companyName = user.company?.name || user.companyName || 'No company assigned';
      }
      
      setFormData({ userName, companyId, companyName, categoryName: '' });
    }
  }, [user, isSuperAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.companyId) {
      newErrors.companyId = isSuperAdmin 
        ? 'Please select a company from the navbar dropdown first'
        : 'You are not assigned to any company';
    }
    
    if (!formData.categoryName.trim()) {
      newErrors.categoryName = 'Category name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await categoryService.createCategory({
        name: formData.categoryName.trim(),
        companyId: parseInt(formData.companyId),
      });

      if (response.success) {
        alert('Category added successfully!');
        navigate('/product/view');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add category');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-category-page">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard/overview">Dashboard</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/product/view">Products</a>
          </li>
          <li className="breadcrumb-item active">Add Category</li>
        </ol>
      </nav>

      {/* Page Header */}
      <div className="page-header">
        <h4 className="page-title">Add New Category</h4>
      </div>

      {/* Form Card */}
      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {/* Category Information */}
          <div className="form-section">
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">User Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.userName}
                  readOnly
                  disabled
                  style={{ backgroundColor: '#f7fafc' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Company <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`form-input ${errors.companyId ? 'error' : ''}`}
                  value={formData.companyName}
                  readOnly
                  disabled
                  style={{ 
                    backgroundColor: formData.companyId ? '#e6fffa' : '#fff5f5',
                    color: formData.companyId ? '#234e52' : '#c53030',
                    fontWeight: '500'
                  }}
                />
                {errors.companyId && (
                  <span className="error-message">{errors.companyId}</span>
                )}
                {isSuperAdmin && !formData.companyId && (
                  <small style={{ color: '#e53e3e', fontSize: '12px' }}>
                    ⚠️ Select company from navbar first
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Category Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="categoryName"
                  className={`form-input ${errors.categoryName ? 'error' : ''}`}
                  value={formData.categoryName}
                  onChange={handleChange}
                  placeholder="Enter category name"
                  disabled={!formData.companyId}
                />
                {errors.categoryName && (
                  <span className="error-message">{errors.categoryName}</span>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/product/view')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || !formData.companyId}
            >
              {isSubmitting ? 'Adding Category...' : 'Add Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCategory;